import type { VSidebarItemProps } from "@verbleif/lib/src/components/Sidebar/VSidebarItem.type";
import type { PinnedFilter } from "@verbleif/lib/src/composables/Api/Type/Pin/pin.interface";
import type { PropertyHydraItem } from "@verbleif/lib/src/composables/Api/Type/Property/property.interface";
import type { PropertyRouteHydraCollectionItem } from "@verbleif/lib/src/composables/Api/Type/PropertyRoute/propertyRoute.interface";
import type { TaskViewRequiredScope } from "@verbleif/lib/src/composables/Api/Type/TaskView/taskView.interface";
import type { UserHydraItem } from "@verbleif/lib/src/composables/Api/Type/User/user.interface";
import type { UserGroupHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserGroup/user_group.interface";
import type { TaskViewChoice } from "@web/features/Tasks/Views/taskViewChoice";
import type { ComputedRef } from "vue";
import type { LocationQueryValue } from "vue-router";
import { iconFromStoredName } from "@verbleif/lib/src/components/Icon/storedIconNames";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { DefaultViewReference } from "@verbleif/lib/src/composables/Views/DefaultViewReference";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { shortcutVoter } from "@verbleif/shared/src/composables/Permissions/SharedPermissions";
import { effectiveDefaultView } from "@verbleif/shared/src/composables/ScopedViews/defaultView.helpers";
import { resolveSidebarIconColor } from "@verbleif/shared/src/composables/ScopedViews/resolveViewIconColor";
import { resolveViewSidebarLabel } from "@verbleif/shared/src/composables/ScopedViews/resolveViewSidebarLabel";
import {
  TASK_VIEW_DEFAULT_ICON,
  TASK_VIEW_DEFAULT_ICON_COLOR_CLASS,
  TASK_VIEW_SIDEBAR_LABEL_KEYS,
} from "@verbleif/shared/src/composables/ScopedViews/taskViewSidebarLabels";
import { useSharedStore } from "@verbleif/shared/src/composables/SharedStore/useSharedStore";
import { useSystemStore } from "@verbleif/shared/src/composables/SystemStore/useSystemStore";
import { TASK_LIST_SCOPE_DEPARTMENT_ICON, TASK_LIST_SCOPE_USER_ICON } from "@verbleif/shared/src/composables/TaskListScope/taskListScopeTree";
import { useTaskListScopeTreeData } from "@verbleif/shared/src/composables/TaskListScope/useTaskListScopeTreeData";
import { useTaskViews } from "@verbleif/shared/src/composables/TaskViews/useTaskViews";
import { createInjectionState } from "@vueuse/core";
import { groupActiveProperties } from "@web/features/Tasks/Overview/Sidebar/propertySidebarRows";
import { BUILTIN_TASK_VIEW_KEYS, hasTaskViewChoice, isBuiltinTaskViewKey, showsInViewsSection, TaskListType } from "@web/features/Tasks/TaskViewKeys";
import { BUILTIN_TASK_VIEW_DEFINITIONS } from "@web/features/Tasks/Views/builtinTaskViewDefinitions";
import { taskViewChoiceFromScope, taskViewChoiceToDefaultScope } from "@web/features/Tasks/Views/taskViewChoice";
import {
  isPropertyBrowseQuery,
  parseTaskViewScopeFromQuery,
  TASK_VIEW_SCOPE_QUERY,
} from "@web/features/Tasks/Views/taskViewScopeQuery";
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

const [useProvideTaskList, useTaskListRaw] = createInjectionState(() => {
  const route = useRoute();
  const { t, te } = useI18n();
  const { views, enabledViews, byKey, viewManagement, switcherEnabled } = useTaskViews();
  const {
    properties,
    propertyGroupsIndexedByIri,
    activePropertyRoutes,
  } = useSystemStore();
  const {
    usersList,
    userGroupsList,
  } = useSharedStore();
  const { userPreferences } = useAuthStore();
  const scopeTree = useTaskListScopeTreeData();

  const hasTaskListsShortcutUserGroups = computed<boolean>(() => {
    return shortcutVoter.hasShortcut({
      attribute: PermissionAttributesEnum.ContextTaskshortcutUsergroups,
    });
  });

  const hasTaskListsUsers = computed<boolean>(() => {
    return shortcutVoter.hasShortcut({
      attribute: PermissionAttributesEnum.ContextTaskshortcutUser,
    });
  });

  const hasTaskListsShortcutUserGroupsLessUser = computed<boolean>(() => {
    return shortcutVoter.hasShortcut({
      attribute: PermissionAttributesEnum.ContextTaskshortcutUsergrouplessuser,
    });
  });

  const showUserGroupsListsTab = computed<boolean>(() => {
    return hasTaskListsShortcutUserGroups.value || hasTaskListsShortcutUserGroupsLessUser.value;
  });

  /**
   * The department lists of the sidebar without view management: the departments and members the
   * reader may read (the same rules as the department tree of model A), limited by the shortcuts of the role.
   */
  const lists = computed<VSidebarItemProps[]>(() => {
    const rawLists: VSidebarItemProps[] = [];
    [...scopeTree.departments.value].sort((a, b) => a.name.localeCompare(b.name)).forEach((department) => {
      const members = hasTaskListsUsers.value ? [...department.members].sort((a, b) => a.name.localeCompare(b.name)) : [];
      rawLists.push({
        routeId: `${TaskListType.TYPE_USER_GROUP}_${department.id}`,
        text: department.name,
        icon: department.icon ?? TASK_LIST_SCOPE_DEPARTMENT_ICON,
        to: {
          name: "task-list",
          query: { view: BUILTIN_TASK_VIEW_KEYS.ALL, [TASK_VIEW_SCOPE_QUERY.userGroup]: String(department.id) },
        },
        children: members.map(member => ({
          routeId: `${TaskListType.TYPE_USER}_${member.id}`,
          text: member.name,
          icon: TASK_LIST_SCOPE_USER_ICON,
          to: { name: "task-list", query: { view: BUILTIN_TASK_VIEW_KEYS.ALL, [TASK_VIEW_SCOPE_QUERY.user]: String(member.id) } },
          children: [],
        })),
      });
    });

    if (hasTaskListsShortcutUserGroupsLessUser.value) {
      [...scopeTree.groupless.value].sort((a, b) => a.name.localeCompare(b.name)).forEach((member) => {
        rawLists.push({
          routeId: `${TaskListType.TYPE_USER}_${member.id}`,
          text: member.name,
          icon: TASK_LIST_SCOPE_USER_ICON,
          to: { name: "task-list", query: { view: BUILTIN_TASK_VIEW_KEYS.ALL, [TASK_VIEW_SCOPE_QUERY.user]: String(member.id) } },
          children: [],
        });
      });
    }
    return rawLists;
  });

  function firstQueryValue(value: LocationQueryValue | LocationQueryValue[] | undefined): string | null {
    const single = Array.isArray(value) ? value[0] : value;
    return typeof single === "string" && single !== "" ? single : null;
  }
  const currentViewKey = computed(() => firstQueryValue(route.query.view));
  const scope = computed(() => parseTaskViewScopeFromQuery(route.query as Record<string, unknown>));
  const currentView = computed(() => {
    const key = currentViewKey.value;
    if (!key) {
      return null;
    }
    return byKey.value.get(key) ?? null;
  });
  const currentViewDefinition = computed(() => currentView.value?.definition ?? null);
  /** The `pinned` filter of the current view, with the same built-in fallback as the list parameters. */
  const currentPinnedFilter = computed<PinnedFilter>(() => {
    const key = currentViewKey.value;
    const definition = currentViewDefinition.value ?? (key ? BUILTIN_TASK_VIEW_DEFINITIONS[key] : null);
    return definition?.filters.pinned ?? "any";
  });
  const isCustomView = computed(() => {
    const key = currentViewKey.value;
    return key != null && !isBuiltinTaskViewKey(key);
  });

  const isPropertyBrowseMode = computed(() => isPropertyBrowseQuery(route.query as Record<string, unknown>));

  // A user inside a department (view management) is that user's list: the department only marks where it was chosen.
  const currentListType = computed<TaskListType | null>(() => {
    if (isPropertyBrowseMode.value) {
      return TaskListType.TYPE_PER_PROPERTY;
    }
    if (scope.value.user) {
      return TaskListType.TYPE_USER;
    }
    if (scope.value.userGroup) {
      return TaskListType.TYPE_USER_GROUP;
    }
    const view = currentViewKey.value;
    if (!view) {
      return null;
    }
    if (view === BUILTIN_TASK_VIEW_KEYS.WALK_ROUTE) {
      return TaskListType.TYPE_WALK_ROUTE;
    }
    if (view === BUILTIN_TASK_VIEW_KEYS.ROUTE) {
      return TaskListType.TYPE_ROUTE;
    }
    return view as TaskListType;
  });

  const currentListId = computed<string | null>(() => {
    if (scope.value.property) {
      return scope.value.property;
    }
    if (scope.value.user) {
      return scope.value.user;
    }
    if (scope.value.userGroup) {
      return scope.value.userGroup;
    }
    if (scope.value.propertyRoute) {
      return scope.value.propertyRoute;
    }
    return firstQueryValue(route.query.id);
  });

  /**
   * Without view management, views that need a scope (walk routes, task lists, objects) cannot be
   * linked from the flat list; their own sidebar sections render them with the scope attached. With
   * it, they are rows in Weergaven that open the choice first.
   */
  const effectiveViewSidebarItems = computed<VSidebarItemProps[]>(() => {
    return enabledViews.value
      .filter(view => showsInViewsSection(view, viewManagement.value))
      .map(view => ({
        routeId: view.key,
        text: resolveViewSidebarLabel(view, TASK_VIEW_SIDEBAR_LABEL_KEYS, t, te),
        icon: iconFromStoredName(view.avatarIconIdentifier, TASK_VIEW_DEFAULT_ICON),
        ...resolveSidebarIconColor(view.avatarColor, TASK_VIEW_DEFAULT_ICON_COLOR_CLASS),
        to: { name: "task-list", query: { view: view.key } },
        children: [],
      }));
  });

  /** The scope the current view asks for (an object, a walk route or a department), or null. */
  const currentRequiredScope = computed<TaskViewRequiredScope | null>(() => {
    const key = currentViewKey.value;
    if (!key) {
      return null;
    }
    return (byKey.value.get(key)?.definition ?? BUILTIN_TASK_VIEW_DEFINITIONS[key])?.requiredScope ?? null;
  });

  /** The choice of the current view as the URL holds it (view management on). */
  const currentChoice = computed<TaskViewChoice | null>(() => {
    const required = currentRequiredScope.value;
    if (!viewManagement.value || required === null) {
      return null;
    }
    return taskViewChoiceFromScope(required, scope.value);
  });

  /** A view that asks for a choice is open without one: Tasks asks for it first. */
  const choiceMissing = computed(() => viewManagement.value
    && currentRequiredScope.value !== null
    && !hasTaskViewChoice(currentRequiredScope.value, scope.value));

  function propertyLabel(id: number): string | null {
    const property = properties.value.find(item => item.id === id);
    return property ? property[userPreferences.value?.defaultPropertyName || "name"] || property.name : null;
  }

  function routeLabel(id: number): string | null {
    return activePropertyRoutes.value.find(route => route["@id"].split("/").pop() === String(id))?.name ?? null;
  }

  function userGroupLabel(id: number): string | null {
    return userGroupsList.value.find(userGroup => userGroup.id === id)?.name ?? null;
  }

  function userLabel(id: number): string | null {
    return usersList.value.find(user => user.id === id)?.name ?? null;
  }

  /** The parts of the choice: the department and the user for a task list, else the object or route. */
  const currentChoiceParts = computed<string[]>(() => {
    const choice = currentChoice.value;
    if (!choice) {
      return [];
    }
    if (choice.type === "taskList") {
      return [
        choice.taskList !== null ? userGroupLabel(choice.taskList) : null,
        choice.user !== null ? userLabel(choice.user) : null,
      ].filter((part): part is string => part !== null);
    }
    const label = choice.type === "property" ? propertyLabel(choice.id) : routeLabel(choice.id);
    return label === null ? [] : [label];
  });

  /** The choice next to the list title: "Schoonmaak › Eva Smit" for a user inside a department. */
  const currentChoiceLabel = computed(() => currentChoiceParts.value.join(" › "));

  /** The choice on the right of the sidebar row: only the user's name for a user inside a department. */
  const currentChoiceShortLabel = computed(() => currentChoiceParts.value.at(-1) ?? "");

  /** The label of the current view as the sidebar shows it ("Per afdeling"). */
  const currentViewLabel = computed(() => {
    const view = currentView.value;
    return view ? resolveViewSidebarLabel(view, TASK_VIEW_SIDEBAR_LABEL_KEYS, t, te) : "";
  });

  /** The open list is the default list: the same view with the same choice, the user in a department included. */
  const isCurrentDefault = computed(() => {
    const key = currentViewKey.value;
    const reference = effectiveDefaultView(views.value);
    if (!key || !reference) {
      return false;
    }
    if (currentRequiredScope.value === null) {
      return reference.viewKey === key && reference.scope === null;
    }
    const choice = currentChoice.value;
    const choiceScope = choice ? taskViewChoiceToDefaultScope(choice) : null;
    return choiceScope !== null && reference.equals(DefaultViewReference.create(key, choiceScope));
  });

  const listQueryReady = computed(() => {
    const key = currentViewKey.value;
    if (!key) {
      return false;
    }
    const effective = byKey.value.get(key);
    const required = effective?.definition.requiredScope ?? null;
    if (!required) {
      return true;
    }
    if (viewManagement.value) {
      return hasTaskViewChoice(required, scope.value);
    }
    if (required === "propertyRoute") {
      return scope.value.propertyRoute != null;
    }
    if (required === "taskList") {
      return scope.value.userGroup != null;
    }
    return true;
  });

  const currentListRouteId = computed(() => {
    if (!currentListType.value) {
      return null;
    }
    return `${currentListType.value}_${currentListId.value}`;
  });

  const currentList = computed<UserHydraItem | UserGroupHydraItem | PropertyHydraItem | PropertyRouteHydraCollectionItem | null>(() => {
    switch (currentListType.value) {
      case TaskListType.TYPE_ALL:
      case TaskListType.TYPE_PER_PROPERTY:
        return properties.value.find((property) => {
          return `${TaskListType.TYPE_PER_PROPERTY}_${property.id}` === currentListRouteId.value;
        }) || null;
      case TaskListType.TYPE_PINNED:
      case TaskListType.TYPE_TODAY:
      case TaskListType.TYPE_OPEN_UNTIL_TODAY:
      case TaskListType.TYPE_ALL_UNTIL_TODAY:
      case TaskListType.TYPE_ALL_TODAY:
      case TaskListType.TYPE_ASSIGNED_TO_ME:
      case TaskListType.TYPE_UNASSIGNED:
      case TaskListType.TYPE_READY:
      case TaskListType.TYPE_FINISHED:
      case TaskListType.TYPE_ROUTE:
        return null;
      case TaskListType.TYPE_WALK_ROUTE:
        return activePropertyRoutes.value.find((route) => {
          const routeId = route["@id"].split("/").pop();
          return scope.value.propertyRoute === routeId
            || `${TaskListType.TYPE_WALK_ROUTE}_${routeId}` === currentListRouteId.value;
        }) || null;
      case TaskListType.TYPE_USER_GROUP:
        return userGroupsList.value.find((userGroup) => {
          return `${TaskListType.TYPE_USER_GROUP}_${userGroup.id}` === currentListRouteId.value;
        }) || null;
      case TaskListType.TYPE_USER:
        return usersList.value.find((user) => {
          return `${TaskListType.TYPE_USER}_${user.id}` === currentListRouteId.value;
        }) || null;
      default:
        return null;
    }
  });

  const loading = computed(() => {
    return !!currentList.value;
  });

  const currentListName = computed<string>(() => {
    if (currentList.value) {
      if (currentList.value["@type"] === "Property") {
        return currentList.value[userPreferences.value?.defaultPropertyName || "name"] || "";
      }

      if (currentList.value["@type"] === "PropertyRoute") {
        return currentList.value.name || "";
      }

      return currentList.value?.name || "";
    }

    if (isCustomView.value) {
      return currentView.value?.name ?? currentViewKey.value ?? "";
    }

    return "";
  });

  const currentListTranslation = computed(() => {
    if (!currentListType.value) {
      return "unknown";
    }
    const map: Partial<Record<TaskListType, string>> = {
      [TaskListType.TYPE_ALL]: "tasks.type.all",
      [TaskListType.TYPE_PINNED]: "tasks.type.pinned",
      [TaskListType.TYPE_PER_PROPERTY]: "tasks.type.per_property",
      [TaskListType.TYPE_TODAY]: "tasks.type.today",
      [TaskListType.TYPE_OPEN_UNTIL_TODAY]: "tasks.type.open_until_today",
      [TaskListType.TYPE_ALL_UNTIL_TODAY]: "tasks.type.all_until_today",
      [TaskListType.TYPE_ALL_TODAY]: "tasks.type.all_today",
      [TaskListType.TYPE_ASSIGNED_TO_ME]: "tasks.type.assigned_to_me",
      [TaskListType.TYPE_UNASSIGNED]: "tasks.type.unassigned",
      [TaskListType.TYPE_READY]: "tasks.type.ready",
      [TaskListType.TYPE_FINISHED]: "tasks.type.finished",
      [TaskListType.TYPE_USER_GROUP]: "tasks.type.user_group",
      [TaskListType.TYPE_USER]: "tasks.type.user",
      [TaskListType.TYPE_ROUTE]: "tasks.type.route",
      [TaskListType.TYPE_WALK_ROUTE]: "tasks.type.walk_route",
    };
    return map[currentListType.value] ?? "tasks.type.custom";
  });

  const propertyTabs = computed<VSidebarItemProps[]>(() => {
    const groups = groupActiveProperties(
      properties.value,
      propertyGroupsIndexedByIri.value,
      property => property[userPreferences.value?.defaultPropertyName || "name"] ?? "",
      group => group[userPreferences.value?.defaultPropertyGroupName || "name"] ?? null,
    );
    return groups.map(group => ({
      routeId: `${TaskListType.TYPE_PER_PROPERTY}_group_${group.groupId}`,
      text: group.groupName,
      icon: "layout-dashboard",
      iconColor: "text-gray-500",
      children: group.properties.map(property => ({
        routeId: `${TaskListType.TYPE_PER_PROPERTY}_${property.id}`,
        text: property.name,
        icon: "house",
        to: {
          name: "task-list",
          query: {
            view: BUILTIN_TASK_VIEW_KEYS.ALL,
            [TASK_VIEW_SCOPE_QUERY.property]: String(property.id),
          },
        },
        children: [],
        iconColor: "text-gray-800 dark:text-gray-100",
      })),
    }));
  });

  const walkRouteTabs = computed<VSidebarItemProps[]>(() => {
    return activePropertyRoutes.value.map((route: PropertyRouteHydraCollectionItem) => {
      const routeId = route["@id"].split("/").pop();
      return {
        routeId: `${TaskListType.TYPE_WALK_ROUTE}_${routeId}`,
        text: route.name,
        icon: iconFromStoredName(route.avatarIconIdentifier, "route"),
        to: {
          name: "task-list",
          query: {
            view: BUILTIN_TASK_VIEW_KEYS.WALK_ROUTE,
            [TASK_VIEW_SCOPE_QUERY.propertyRoute]: String(routeId),
          },
        },
        children: [],
      };
    });
  });

  const visiblePropertyTabs = reactive<Record<string, boolean>>({});

  return {
    lists,
    visiblePropertyTabs,
    currentListName,
    currentList,
    currentListRouteId,
    currentListId,
    propertyTabs,
    walkRouteTabs,
    currentListType,
    currentViewKey,
    currentViewDefinition,
    currentPinnedFilter,
    scope,
    listQueryReady,
    effectiveViewSidebarItems,
    isPropertyBrowseMode,
    currentListTranslation,
    loading,
    hasTaskListsShortcutUserGroups,
    hasTaskListsUsers,
    hasTaskListsShortcutUserGroupsLessUser,
    showUserGroupsListsTab,
    viewManagement,
    switcherEnabled,
    currentRequiredScope,
    currentChoice,
    choiceMissing,
    currentChoiceLabel,
    currentChoiceShortLabel,
    currentViewLabel,
    isCurrentDefault,
  };
});

export { useProvideTaskList, useTaskListRaw };

export function useTaskList() {
  const taskList = useTaskListRaw();
  if (taskList == null) {
    throw new Error("Please call `useProvideTaskList` on the appropriate parent component");
  }
  return taskList;
}

export interface TaskListScope {
  currentListType: ComputedRef<TaskListType | null>
  currentListId: ComputedRef<string | null>
}

const NO_TASK_LIST_SCOPE: TaskListScope = {
  currentListType: computed(() => null),
  currentListId: computed(() => null),
};

/**
 * Task views also render outside a task list, e.g. TaskHistory in the root dialog host.
 * There no list is selected, so both type and id resolve to null.
 */
export function useTaskListScope(): TaskListScope {
  return useTaskListRaw() ?? NO_TASK_LIST_SCOPE;
}

export const isFocusedOnTaskShortcut = computed(() => {
  return shortcutVoter.isFocusedOnTaskShortcut();
});
